import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { UsersService } from '../api/services/users.service';
import { TasksService } from '../api/services/tasks.service';
import { ProjectsService } from '../api/services/projects.service';
import NcLinkPreview, { isNcUrl } from './NcLinkPreview';

// Global cache
let cachedUsers = null;
let cachedTasks = null;
let cachedProjects = null;
let fetchingPromise = null;

export default function LinkifyText({ text, style }) {
  const [dataLoaded, setDataLoaded] = useState(!!(cachedUsers && cachedTasks && cachedProjects));

  useEffect(() => {
    if (!dataLoaded) {
      if (!fetchingPromise) {
        fetchingPromise = Promise.all([
          UsersService.getUsers().catch(() => ({ data: [] })),
          TasksService.getTasks().catch(() => ({ data: [] })),
          ProjectsService.getProjects().catch(() => ({ data: [] }))
        ]).then(([usersRes, tasksRes, projectsRes]) => {
          cachedUsers = usersRes.data || [];
          cachedTasks = tasksRes.data || [];
          cachedProjects = projectsRes.data || [];
          return true;
        });
      }
      fetchingPromise.then(() => {
        setDataLoaded(true);
      });
    }
  }, [dataLoaded]);

  if (!text) return null;
  const strText = String(text);

  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const markdownLinkRegex = /\[(.*?)\]\((.*?)\)/g;

  // Ссылка Nextcloud на своей строке → полная карточка-превью, внутри текста → чип
  const ncBlock = (part) => {
    if (!isNcUrl(part)) return null;
    const lines = strText.split('\n');
    const standalone = lines.some((l) => l.trim() === part);
    return <NcLinkPreview url={part} block={standalone} />;
  };

  if (!dataLoaded) {
    // Fallback: only links if data is not loaded yet
    const combinedFallbackRegex = /(https?:\/\/[^\s]+|\[.*?\]\(.*?\))/g;
    const parts = strText.split(combinedFallbackRegex).filter(p => p !== undefined && p !== '');
    return (
      <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', ...style }}>
        {parts.map((part, i) => {
          if (part.match(urlRegex)) {
            const nc = ncBlock(part);
            if (nc) return <React.Fragment key={i}>{nc}</React.Fragment>;
            return (
              <a key={i} href={part} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                {part}
              </a>
            );
          }
          const mdMatch = markdownLinkRegex.exec(part);
          if (mdMatch) {
             markdownLinkRegex.lastIndex = 0; // reset
             const href = mdMatch[2];
             const label = mdMatch[1];
             if (href.startsWith('/')) {
               return (
                 <Link key={i} to={href} style={{ fontWeight: 'bold', color: '#1890ff' }} onClick={(e) => e.stopPropagation()}>
                   {label}
                 </Link>
               );
             }
             return (
               <a key={i} href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                 {label}
               </a>
             );
          }
          return <span key={i}>{part}</span>;
        })}
      </span>
    );
  }

  const userNames = cachedUsers.map(u => u.full_name || u.email).filter(Boolean).sort((a, b) => b.length - a.length);
  const taskTitles = cachedTasks.map(t => t.title).filter(Boolean).sort((a, b) => b.length - a.length);
  const projectNames = cachedProjects.map(p => p.name).filter(Boolean).sort((a, b) => b.length - a.length);

  const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const userRegexStr = userNames.length > 0 ? userNames.map(escapeRegExp).join('|') : 'a^';
  const taskRegexStr = taskTitles.length > 0 ? taskTitles.map(escapeRegExp).join('|') : 'a^';
  const projRegexStr = projectNames.length > 0 ? projectNames.map(escapeRegExp).join('|') : 'a^';

  // Combine URL regex with mentions regex and markdown links
  const combinedRegex = new RegExp(`(\\[.*?\\]\\(.*?\\))|(https?:\\/\\/[^\\s]+)|(@(?:${userRegexStr}))|(#(?:${taskRegexStr}))|(\\$(?:${projRegexStr}))|(#\\d+)|(\\$\\d+)|(@\\d+)`, 'g');

  const parts = strText.split(combinedRegex).filter(p => p !== undefined && p !== '');

  return (
    <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', ...style }}>
      {parts.map((part, index) => {
        if (part.match(urlRegex)) {
          const nc = ncBlock(part);
          if (nc) return <React.Fragment key={index}>{nc}</React.Fragment>;
          return (
            <a key={index} href={part} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
              {part}
            </a>
          );
        }
        
        const mdMatch = markdownLinkRegex.exec(part);
        if (mdMatch) {
            markdownLinkRegex.lastIndex = 0; // reset
            const href = mdMatch[2];
            const label = mdMatch[1];
            if (href.startsWith('/')) {
              return (
                <Link key={index} to={href} style={{ fontWeight: 'bold', color: '#1890ff' }} onClick={(e) => e.stopPropagation()}>
                  {label}
                </Link>
              );
            }
            return (
              <a key={index} href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                {label}
              </a>
            );
        }
        
        if (part.startsWith('@')) {
          const nameOrId = part.substring(1);
          let userMatch = cachedUsers.find(u => (u.full_name || u.email) === nameOrId);
          if (!userMatch && /^\d+$/.test(nameOrId)) {
            userMatch = cachedUsers.find(u => String(u.id) === nameOrId);
            if (!userMatch) {
              return <Link key={index} to={`/users?userId=${nameOrId}`} style={{ fontWeight: 'bold' }}>{part}</Link>;
            }
          }
          if (userMatch) {
            return <Link key={index} to={`/users?userId=${userMatch.id}`} style={{ fontWeight: 'bold' }}>{part}</Link>;
          }
        }
        
        if (part.startsWith('#')) {
          const titleOrId = part.substring(1);
          let taskMatch = cachedTasks.find(t => t.title === titleOrId);
          if (!taskMatch && /^\d+$/.test(titleOrId)) {
            taskMatch = cachedTasks.find(t => String(t.id) === titleOrId);
            if (!taskMatch) {
              return <Link key={index} to={`/tasks?taskId=${titleOrId}`} style={{ fontWeight: 'bold' }}>{part}</Link>;
            }
          }
          if (taskMatch) {
            return <Link key={index} to={`/tasks?taskId=${taskMatch.id}`} style={{ fontWeight: 'bold' }}>{part}</Link>;
          }
        }
        
        if (part.startsWith('$')) {
          const nameOrId = part.substring(1);
          let projMatch = cachedProjects.find(p => p.name === nameOrId);
          if (!projMatch && /^\d+$/.test(nameOrId)) {
            projMatch = cachedProjects.find(p => String(p.id) === nameOrId);
            if (!projMatch) {
              return <Link key={index} to={`/projects/${nameOrId}`} style={{ fontWeight: 'bold' }}>{part}</Link>;
            }
          }
          if (projMatch) {
            return <Link key={index} to={`/projects/${projMatch.id}`} style={{ fontWeight: 'bold' }}>{part}</Link>;
          }
        }
        
        return <span key={index}>{part}</span>;
      })}
    </span>
  );
}